// frontend/src/pages/HomeDashboard.js
//
// Landing page after login. Greets the user, shows a live clock, and lists
// every page they can access (grouped the same way as the sidebar) so they
// can jump straight to where they need to be.

import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardActionArea,
  Avatar,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import { AdminPanelSettings, AccountCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import { getVisibleSections } from '../config/menuSections';
import Navigation from '../components/Navigation';
import { useSidebarPrefs } from '../contexts/SidebarPrefsContext';

// Nav entries that make no sense as a "go to" card from the landing page
const EXCLUDED_KEYS = new Set(['dashboard']);

const getGreeting = (date) => {
  const hour = date.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const HomeDashboard = () => {
  const { user, hasPageAccess } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const { pinnedActive } = useSidebarPrefs();
  const [now, setNow] = useState(() => new Date());

  usePageTitle('Dashboard');

  // Tick once a second for the clock
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const isAdmin = user?.is_staff || user?.role === 'admin';
  const displayName = user?.first_name || user?.username || 'there';

  const sections = getVisibleSections({ isAdmin, hasPageAccess })
    .map(section => ({
      ...section,
      items: section.items.filter(item => !EXCLUDED_KEYS.has(item.key))
    }))
    .filter(section => section.items.length > 0);

  const timeString = now.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
  const dateString = now.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', minHeight: 'calc(100vh - 64px)' }}>
      {/* Docked sidebar (desktop only; the navbar hamburger covers smaller screens) */}
      {!pinnedActive && (
        <Box sx={{ display: { xs: 'none', md: 'block' }, p: { md: 3 }, pr: { md: 0 } }}>
          <Navigation variant="docked" />
        </Box>
      )}

    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, flex: 1, minWidth: 0 }}>
      {/* Header: greeting + clock */}
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2.5, md: 4 },
          mb: 4,
          borderRadius: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${theme.palette.background.paper} 60%)`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: 'primary.main',
              fontSize: 28,
              fontWeight: 700
            }}
          >
            {displayName[0]?.toUpperCase() || 'U'}
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {getGreeting(now)}, {displayName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={isAdmin ? 'Administrator' : user?.role || 'User'}
                color={isAdmin ? 'primary' : 'secondary'}
                icon={isAdmin ? <AdminPanelSettings /> : <AccountCircle />}
              />
              {user?.department && (
                <Typography variant="body2" color="text.secondary">
                  {user.department}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
          <Typography
            variant="h3"
            fontWeight={700}
            sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}
          >
            {timeString}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {dateString}
          </Typography>
        </Box>
      </Paper>

      {/* Page shortcuts, grouped like the sidebar */}
      {sections.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="body1" color="text.secondary">
            You don't have access to any pages yet. Contact your administrator.
          </Typography>
        </Paper>
      ) : (
        sections.map(section => {
          // Dashboard cards are always colour-coded; the toggle only affects the sidebar
          const accent = section.color || theme.palette.primary.main;
          return (
          <Box key={section.title || 'general'} sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 6, height: 22, borderRadius: 1, bgcolor: accent }} />
              <Typography
                variant="overline"
                sx={{ fontWeight: 700, letterSpacing: 1, color: accent, lineHeight: 1 }}
              >
                {section.title || 'General'}
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {section.items.map(item => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item.key}>
                  <Card
                    elevation={1}
                    sx={{
                      height: '100%',
                      borderRadius: 2,
                      borderLeft: `4px solid ${accent}`,
                      background: `linear-gradient(135deg, ${alpha(accent, 0.06)} 0%, ${theme.palette.background.paper} 70%)`,
                      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[6]
                      }
                    }}
                  >
                    <CardActionArea
                      onClick={() => navigate(item.path)}
                      sx={{
                        height: '100%',
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: 2
                      }}
                    >
                      <Avatar
                        variant="rounded"
                        sx={{
                          bgcolor: alpha(accent, 0.15),
                          color: accent,
                          width: 44,
                          height: 44
                        }}
                      >
                        {item.icon}
                      </Avatar>
                      <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {item.text}
                      </Typography>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
          );
        })
      )}
    </Container>
    </Box>
  );
};

export default HomeDashboard;
